import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent } from "react";
import { formatarReais } from "@caixaflow/shared";
import { api, ErroApi, qs } from "../../../api/cliente";
import type { Categoria, Produto } from "../../../api/tipos";
import { Campo, EntradaReais } from "../../../components/Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "../../../components/Estados";
import { Modal } from "../../../components/Modal";
import { useToast } from "../../../components/Toast";

export function AbaCatalogo() {
  const [verArquivados, setVerArquivados] = useState(false);
  const [categoriaEditada, setCategoriaEditada] = useState<Categoria | "nova" | null>(null);
  const [produtoEditado, setProdutoEditado] = useState<Produto | "novo" | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const categorias = useQuery({ queryKey: ["categorias"], queryFn: () => api<Categoria[]>("/categorias") });
  const produtos = useQuery({
    queryKey: ["produtos", { verArquivados }],
    queryFn: () => api<Produto[]>(`/produtos${qs({ incluirExcluidos: verArquivados || undefined })}`),
  });

  const recarregar = () => {
    qc.invalidateQueries({ queryKey: ["produtos"] });
    qc.invalidateQueries({ queryKey: ["categorias"] });
  };
  const excluirProduto = useMutation({
    mutationFn: (id: string) => api<{ resultado: string; mensagem: string }>(`/produtos/${id}`, "DELETE"),
    onSuccess: (r) => {
      toast(r.mensagem, r.resultado === "ARQUIVADO" ? "info" : "ok");
      recarregar();
    },
    onError: (e) => toast(mensagemDeErro(e), "erro"),
  });
  const excluirCategoria = useMutation({
    mutationFn: (id: string) => api(`/categorias/${id}`, "DELETE"),
    onSuccess: () => {
      toast("Categoria excluída");
      recarregar();
    },
    onError: (e) => toast(mensagemDeErro(e), "erro"),
  });

  const porCategoria = new Map<string, Produto[]>();
  for (const p of produtos.data ?? []) {
    const lista = porCategoria.get(p.categoria.nome) ?? [];
    lista.push(p);
    porCategoria.set(p.categoria.nome, lista);
  }

  return (
    <>
      <section>
        <div className="linha" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Produtos</h2>
          <div className="linha-esq">
            <label className="marcar pequeno">
              <input type="checkbox" checked={verArquivados} onChange={(e) => setVerArquivados(e.target.checked)} />
              Mostrar arquivados
            </label>
            <button className="btn primario" onClick={() => setProdutoEditado("novo")} disabled={!categorias.data?.length}>
              Novo produto
            </button>
          </div>
        </div>
        {produtos.isPending && <Carregando />}
        {produtos.error && <FalhaAoCarregar erro={produtos.error} tentarDeNovo={() => produtos.refetch()} />}
        {produtos.data && (
          <div className="card tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Produto</th>
                  <th className="r">Preço</th>
                  <th className="r">Alerta em</th>
                  <th>Situação</th>
                  <th className="r">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {produtos.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="vazio">
                      Nenhum produto cadastrado.
                    </td>
                  </tr>
                )}
                {[...porCategoria.entries()].map(([categoria, lista]) => (
                  <Fragment key={categoria}>
                    <tr className="cat">
                      <td colSpan={5}>{categoria}</td>
                    </tr>
                    {lista.map((p) => (
                      <tr key={p.id} className={p.ativo && !p.excluidoEm ? "" : "inativo"}>
                        <td className="quebra">{p.nome}</td>
                        <td className="r">{formatarReais(p.precoCentavos)}</td>
                        <td className="r">{p.limiteEstoqueBaixo > 0 ? `≤ ${p.limiteEstoqueBaixo}` : "—"}</td>
                        <td>
                          {p.excluidoEm ? (
                            <span className="chip neutro">Arquivado</span>
                          ) : p.ativo ? (
                            <span className="chip ok">Ativo</span>
                          ) : (
                            <span className="chip neutro">Inativo</span>
                          )}
                        </td>
                        <td>
                          {!p.excluidoEm && (
                            <div className="acoes">
                              <button className="link-botao" onClick={() => setProdutoEditado(p)}>
                                Editar
                              </button>
                              <button
                                className="link-botao perigo"
                                disabled={excluirProduto.isPending}
                                onClick={() =>
                                  window.confirm(
                                    `Excluir "${p.nome}"? Se já tiver vendas ou estoque lançado, ele será arquivado (sai do cadastro, mas continua nos relatórios).`,
                                  ) && excluirProduto.mutate(p.id)
                                }
                              >
                                Excluir
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="linha" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Categorias</h2>
          <button className="btn fantasma" onClick={() => setCategoriaEditada("nova")}>
            Nova categoria
          </button>
        </div>
        {categorias.error && <FalhaAoCarregar erro={categorias.error} tentarDeNovo={() => categorias.refetch()} />}
        {categorias.data && (
          <div className="card tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th className="r">Ordem</th>
                  <th className="r">Produtos</th>
                  <th>Situação</th>
                  <th className="r">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {categorias.data.map((c) => (
                  <tr key={c.id} className={c.ativo ? "" : "inativo"}>
                    <td>{c.nome}</td>
                    <td className="r">{c.ordem}</td>
                    <td className="r">{c._count.produtos}</td>
                    <td>{c.ativo ? <span className="chip ok">Ativa</span> : <span className="chip neutro">Inativa</span>}</td>
                    <td>
                      <div className="acoes">
                        <button className="link-botao" onClick={() => setCategoriaEditada(c)}>
                          Editar
                        </button>
                        <button
                          className="link-botao perigo"
                          disabled={excluirCategoria.isPending}
                          onClick={() => window.confirm(`Excluir a categoria "${c.nome}"?`) && excluirCategoria.mutate(c.id)}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        aberto={produtoEditado !== null}
        titulo={produtoEditado === "novo" ? "Novo produto" : "Editar produto"}
        onFechar={() => setProdutoEditado(null)}
      >
        {produtoEditado !== null && (
          <FormProduto
            produto={produtoEditado === "novo" ? null : produtoEditado}
            categorias={categorias.data ?? []}
            onPronto={() => {
              setProdutoEditado(null);
              recarregar();
            }}
          />
        )}
      </Modal>
      <Modal
        aberto={categoriaEditada !== null}
        titulo={categoriaEditada === "nova" ? "Nova categoria" : "Editar categoria"}
        onFechar={() => setCategoriaEditada(null)}
      >
        {categoriaEditada !== null && (
          <FormCategoria
            categoria={categoriaEditada === "nova" ? null : categoriaEditada}
            onPronto={() => {
              setCategoriaEditada(null);
              recarregar();
            }}
          />
        )}
      </Modal>
    </>
  );
}

function FormProduto({
  produto,
  categorias,
  onPronto,
}: {
  produto: Produto | null;
  categorias: Categoria[];
  onPronto: () => void;
}) {
  const [nome, setNome] = useState(produto?.nome ?? "");
  const [categoriaId, setCategoriaId] = useState(produto?.categoriaId ?? categorias.find((c) => c.ativo)?.id ?? "");
  const [preco, setPreco] = useState<number | null>(produto?.precoCentavos ?? null);
  const [limite, setLimite] = useState(String(produto?.limiteEstoqueBaixo ?? 0));
  const [ordem, setOrdem] = useState(String(produto?.ordem ?? 0));
  const [ativo, setAtivo] = useState(produto?.ativo ?? true);
  const toast = useToast();

  const salvar = useMutation({
    mutationFn: () => {
      const dados = {
        nome,
        categoriaId,
        precoCentavos: preco ?? -1,
        limiteEstoqueBaixo: Number(limite) || 0,
        ordem: Number(ordem) || 0,
        ativo,
      };
      return produto ? api(`/produtos/${produto.id}`, "PATCH", dados) : api("/produtos", "POST", dados);
    },
    onSuccess: () => {
      toast(produto ? "Produto atualizado" : "Produto criado");
      onPronto();
    },
  });
  const erro = salvar.error instanceof ErroApi ? salvar.error : null;

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    salvar.mutate();
  };

  return (
    <form className="form" onSubmit={enviar}>
      {salvar.error && !erro?.campos.length && <div className="aviso erro">{mensagemDeErro(salvar.error)}</div>}
      <Campo rotulo="Nome" erro={erro?.doCampo("nome")}>
        <input required value={nome} onChange={(e) => setNome(e.target.value)} />
      </Campo>
      <Campo rotulo="Categoria" erro={erro?.doCampo("categoriaId")}>
        <select value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)} required>
          {categorias.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
              {c.ativo ? "" : " (inativa)"}
            </option>
          ))}
        </select>
      </Campo>
      <div className="duas-colunas">
        <Campo rotulo="Preço (R$)" erro={erro?.doCampo("precoCentavos") && "Informe um preço válido"}>
          <EntradaReais centavos={preco} onChange={setPreco} />
        </Campo>
        <Campo rotulo="Alerta de estoque" dica="avisa quando o saldo chegar a">
          <input type="number" min={0} inputMode="numeric" value={limite} onChange={(e) => setLimite(e.target.value)} />
        </Campo>
      </div>
      <Campo rotulo="Ordem na grade" dica="menor aparece primeiro">
        <input type="number" min={0} inputMode="numeric" value={ordem} onChange={(e) => setOrdem(e.target.value)} />
      </Campo>
      <label className="marcar">
        <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
        <span>Ativo (aparece no PDV dos eventos em que está no cardápio)</span>
      </label>
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Cancelar
        </button>
        <button className="btn primario" type="submit" disabled={salvar.isPending}>
          {salvar.isPending ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </form>
  );
}

function FormCategoria({ categoria, onPronto }: { categoria: Categoria | null; onPronto: () => void }) {
  const [nome, setNome] = useState(categoria?.nome ?? "");
  const [ordem, setOrdem] = useState(String(categoria?.ordem ?? 0));
  const [ativo, setAtivo] = useState(categoria?.ativo ?? true);
  const toast = useToast();

  const salvar = useMutation({
    mutationFn: () => {
      const dados = { nome, ordem: Number(ordem) || 0, ativo };
      return categoria ? api(`/categorias/${categoria.id}`, "PATCH", dados) : api("/categorias", "POST", dados);
    },
    onSuccess: () => {
      toast(categoria ? "Categoria atualizada" : "Categoria criada");
      onPronto();
    },
  });
  const erro = salvar.error instanceof ErroApi ? salvar.error : null;

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        salvar.mutate();
      }}
    >
      {salvar.error && !erro?.campos.length && <div className="aviso erro">{mensagemDeErro(salvar.error)}</div>}
      <Campo rotulo="Nome" erro={erro?.doCampo("nome")}>
        <input required value={nome} onChange={(e) => setNome(e.target.value)} />
      </Campo>
      <Campo rotulo="Ordem" dica="menor aparece primeiro">
        <input type="number" min={0} inputMode="numeric" value={ordem} onChange={(e) => setOrdem(e.target.value)} />
      </Campo>
      <label className="marcar">
        <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
        <span>Ativa</span>
      </label>
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Cancelar
        </button>
        <button className="btn primario" type="submit" disabled={salvar.isPending}>
          {salvar.isPending ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </form>
  );
}
